import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import BasicDropdown from 'ember-basic-dropdown/components/basic-dropdown';
import { type Task } from 'ember-concurrency';
import type Flow from 'garaje/models/flow';
import type SignInField from 'garaje/models/sign-in-field';

interface SignInFieldsFieldOptionsModalSensitiveFieldsComponentSignature {
  Args: {
    signInFields?: Array<SignInField>;
    flow?: Flow;
    saveFieldsTask?: Task<void, []>;
    isDisabled?: boolean;
    isPristine?: boolean;
    onRequestSave?: () => void;
    onDropdownToggle?: (isOpen: boolean) => void;
  };
}

export default class SignInFieldsFieldOptionsModalSensitiveFieldsComponent extends Component<SignInFieldsFieldOptionsModalSensitiveFieldsComponentSignature> {
  optionsName = 'sensitive-fields';

  @tracked searchQuery = '';

  get allFields(): Array<SignInField> {
    return this.args.signInFields ?? [];
  }

  get filteredFields(): Array<SignInField> {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) return this.allFields;
    return this.allFields.filter((field) => (field.name ?? '').toLowerCase().includes(query));
  }

  get sensitiveFields(): Array<SignInField> {
    return this.allFields.filter((field) => field.sensitive);
  }

  get hasSensitiveFields(): boolean {
    return this.sensitiveFields.length > 0;
  }

  isSelected = (field: SignInField): boolean => field.sensitive;

  get isSelectAllDisabled(): boolean {
    return this.allFields.length === 0 || this.sensitiveFields.length === this.allFields.length;
  }

  get isDeselectAllDisabled(): boolean {
    return this.sensitiveFields.length === 0;
  }

  @action
  handleSearch(event: Event): void {
    this.searchQuery = (event.target as HTMLInputElement).value;
  }

  @action
  toggleField(field: SignInField): void {
    field.sensitive = !field.sensitive;
  }

  @action
  removeSensitiveField(field: SignInField): void {
    field.sensitive = false;
  }

  @action
  selectAll(): void {
    this.allFields.forEach((field) => (field.sensitive = true));
  }

  @action
  deselectAll(): void {
    this.allFields.forEach((field) => (field.sensitive = false));
  }

  get isCTAVisible(): boolean {
    return !this.args.isDisabled && typeof this.args.onRequestSave === 'function';
  }

  get isCTADisabled(): boolean {
    return !!this.args.isDisabled || !!this.args.isPristine || !!this.args.saveFieldsTask?.isRunning;
  }

  @action
  handleRequestSave(): void {
    this.args.onRequestSave?.();
  }

  @action
  handleDropdownOpen(): void {
    this.args.onDropdownToggle?.(true);
  }

  @action
  handleDropdownClose(): void {
    this.args.onDropdownToggle?.(false);
    this.searchQuery = '';
  }

  willDestroy(): void {
    super.willDestroy();
    this.args.onDropdownToggle?.(false);
  }

  <template>
    <div class="px-8 overflow-none" data-test-field-options={{this.optionsName}}>
      <p class="text-sm text-carbon-60 mb-6 pr-12">
        Responses will only be visible to roles with the
        <span class="font-semibold">Access sensitive fields</span>
        permission.
      </p>

      <p class="text-sm text-carbon-50 font-bold mb-2">Add fields</p>
      <BasicDropdown
        @matchTriggerWidth={{true}}
        @renderInPlace={{false}}
        @destination="modal-overlays"
        @verticalPosition="auto"
        @disabled={{@isDisabled}}
        @onOpen={{this.handleDropdownOpen}}
        @onClose={{this.handleDropdownClose}}
        as |dropdown|
      >
        <dropdown.Trigger class="ember-power-select-trigger w-full" data-test-sensitive-fields-search>
          <span class="ember-power-select-placeholder">Search fields</span>
        </dropdown.Trigger>
        <dropdown.Content>
          <div class="ember-power-select-dropdown bg-white">
            <div class="py-2 px-2 bg-carbon-5">
              <input
                type="search"
                class="pageSearch rounded-lg border icon-search w-full"
                placeholder="Search fields"
                data-test-sensitive-fields-search-input
                value={{this.searchQuery}}
                {{on "input" this.handleSearch}}
              />
            </div>

            <div class="flex items-center justify-start px-3 py-2 text-sm border-b border-carbon-10">
              <button
                data-test-sensitive-fields-select-all
                type="button"
                class="btn--link !text-gem-50"
                disabled={{this.isSelectAllDisabled}}
                {{on "click" this.selectAll}}
              >
                Select all
              </button>
              <span class="mx-1 select-none text-carbon-50">|</span>
              <button
                data-test-sensitive-fields-deselect-all
                type="button"
                class="btn--link !text-gem-50"
                disabled={{this.isDeselectAllDisabled}}
                {{on "click" this.deselectAll}}
              >
                Deselect all
              </button>
            </div>

            <ul class="ember-power-select-options" style="max-height: 240px; overflow-y: auto;">
              {{#each this.filteredFields as |field|}}
                <li data-test-sensitive-fields-option={{field.id}} class="p-0" tabindex="-1">
                  <label class="ember-power-select-option flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={{this.isSelected field}}
                      {{on "change" (fn this.toggleField field)}}
                    />
                    <span class="text-base {{if (this.isSelected field) 'text-gem-60' 'text-carbon-70'}}">
                      {{field.name}}
                    </span>
                  </label>
                </li>
              {{else}}
                <li class="text-center py-2 text-carbon-40">No results</li>
              {{/each}}
            </ul>
          </div>
        </dropdown.Content>
      </BasicDropdown>

      <p class="text-sm text-carbon-50 font-bold mt-6 mb-2">Marked as sensitive</p>
      <div
        data-test-sensitive-fields-list
        class="border border-dashed border-carbon-40 rounded-md p-4 mb-6 divide-y divide-carbon-10"
      >
        {{#if this.hasSensitiveFields}}
          {{#each this.sensitiveFields as |field|}}
            <div data-test-sensitive-field={{field.id}} class="flex items-center justify-between py-2">
              <span class="text-sm text-carbon-50">{{field.name}}</span>
              <button
                type="button"
                disabled={{@isDisabled}}
                aria-label="Remove {{field.name}} from sensitive fields"
                class="bg-transparent border-0 p-0 text-black text-xl leading-none
                  {{if @isDisabled 'cursor-not-allowed opacity-40' 'cursor-pointer'}}"
                {{on "click" (fn this.removeSensitiveField field)}}
                data-test-remove-sensitive-field={{field.id}}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          {{/each}}
        {{else}}
          <p data-test-empty-text class="text-center text-carbon-40 m-0">
            No sensitive fields yet
          </p>
        {{/if}}
      </div>

      {{#if this.isCTAVisible}}
        <button
          disabled={{this.isCTADisabled}}
          class="btn btn--small btn--brand float-right mb-8 {{if this.isCTADisabled 'disabled'}}"
          {{on "click" this.handleRequestSave}}
          data-test-save-options={{this.optionsName}}
        >
          Save
        </button>
      {{/if}}
    </div>
  </template>
}
